import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Checkbox,
  Menu,
  MenuItem,
} from "@mui/material";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import {
  CustomTreeItem,
  TypographyNormal,
  TypographySmall,
} from "../../../customComponent";
import { inputOnlyNumbers } from "../../../../helpers/inputProcessing";
import { colorStyling } from "../../../../helpers/color";
import { setDuplicateAsset } from "../../../../store/actions/cmsAction";
import { bplrtOptions } from "../../../../externalFeatures/components/bplrt";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutline";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import SettingsIcon from "@mui/icons-material/Settings";
import { red } from "@mui/material/colors";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GroupFolderTooltipIcon,
  GroupFolderOpenTooltipIcon,
} from "../../../SharedComponents";

const ContentItem = ({
  content,
  index,
  removeContent,
  updateInterval,
  updateIsMuted,
  handleOpenAdSetting,
  handleOpenMediaSetting,
  handleOpenBPLRTSetting,
  handleOpenDuplicateDialog,
  handleOpenHTMLComponentSetting,
  assetCount,
}) => {
  const dispatch = useDispatch();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: content.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const { t } = useTranslation("cms");

  const [interval, setInterval] = useState("");
  const [muteVideo, setMuteVideo] = useState(false);

  const [anchorMore, setAnchorMore] = useState(null);
  const openMore = Boolean(anchorMore);

  const handleClickMore = (event) => {
    setAnchorMore(event.currentTarget);
  };

  const handleCloseMore = () => {
    setAnchorMore(null);
  };

  useEffect(() => {
    setInterval(content.interval);
    setMuteVideo(content.isMuted ?? false);
  }, [content]);

  function handleIntervalChange(event) {
    let value = event?.target?.value;
    if (!value || value.length === 0 || parseInt(value) <= 0) {
      value = 1;
    }

    setInterval(value);
    updateInterval(index, value);
  }

  function handleMuteVideoChange(event) {
    let value = event.target.checked;
    setMuteVideo(value);
    updateIsMuted(index, value);
  }

  function handleOpenSetting() {
    switch (content.appType) {
      default:
        handleOpenMediaSetting(content);
        break;

      case "ads":
        handleOpenAdSetting(content);
        break;

      case "html":
        handleOpenHTMLComponentSetting(content);
        break;

      case "bplrt":
        handleOpenBPLRTSetting(content);
        break;
    }
  }

  const startDuplicateAsset = () => {
    handleOpenDuplicateDialog();
    dispatch(setDuplicateAsset(content));
  };

  const contentName = useMemo(() => {
    switch (content.type) {
      case "BPLRT_EQUIPMENT":
      case "BPLRT_EQUIPMENT_TABLE":
      case "BPLRT_SCHEDULE":
      case "BPLRT_SERVICE_INFO":
      case "BPLRT_SMP":
        const appType = content.type;
        return t(`bplrt.component${bplrtOptions[appType].label}`);

      case "GROUPED":
        return content.name;

      case "HTML":
        return `${content.name}.html`;

      default:
        return content.name;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        p: 1,
        mx: 1,
        mt: 1,
        width: "100%",
        borderRadius: "4px",
        ":hover": {
          backgroundColor: "rgba(28, 78, 216, 0.1)",
        },
      }}
      display={"flex"}
      justifyContent={"space-between"}
      alignItems={"center"}
    >
      <Box
        display={"flex"}
        alignItems={"center"}
        width={assetCount > 1 || content.appType === "ads" ? "70%" : "90%"}
      >
        <Tooltip
          arrow={!isDragging}
          disableInteractive
          title={
            isDragging ? (
              ""
            ) : (
              <TypographySmall sx={{ color: "white", textAlign: "center" }}>
                {t("designer.contentOrderTip")}
              </TypographySmall>
            )
          }
        >
          <Box
            minWidth={"48px"}
            display={"flex"}
            alignItems={"center"}
            {...listeners}
            {...attributes}
            sx={{ cursor: "grab" }}
          >
            <Box display={"flex"} alignItems={"center"} sx={{ mx: "4px" }}>
              <UnfoldMoreIcon
                sx={{
                  ":hover": {
                    border: `2px solid ${colorStyling.primary}`,
                    borderRadius: "4px",
                  },
                }}
              />
            </Box>
            <Box sx={{ textAlign: "right" }}>{`${index + 1}.`}</Box>
          </Box>
        </Tooltip>

        <Box
          display={"flex"}
          flexDirection={"column"}
          maxWidth={"85%"}
          sx={{ ml: 1 }}
        >
          {content?.type !== "GROUPED" ? (
            <Tooltip arrow disableInteractive title={contentName}>
              <TypographyNormal
                sx={{
                  fontSize: "15px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {contentName}
              </TypographyNormal>
            </Tooltip>
          ) : (
            <SimpleTreeView disableSelection>
              <CustomTreeItem
                itemId={`${content.assetID}`}
                ExpandIcon={GroupFolderTooltipIcon}
                CollapseIcon={GroupFolderOpenTooltipIcon}
                customRootStyle={{
                  sx: {
                    maxWidth: "255px",
                    position: "relative",
                  },
                }}
                label={
                  <Tooltip arrow disableInteractive title={contentName}>
                    <Box sx={{ maxWidth: "250px" }}>
                      <TypographyNormal
                        sx={{
                          fontSize: "15px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {contentName}
                      </TypographyNormal>
                    </Box>
                  </Tooltip>
                }
                sx={{ maxWidth: "300px" }}
              >
                {content.children?.length > 0 &&
                  content.children.map((child) => (
                    <CustomTreeItem
                      key={`${child.id}`}
                      itemId={`${child.id}`}
                      customRootStyle={{
                        sx: {
                          maxWidth: "255px",
                          position: "relative",
                        },
                      }}
                      customContentStyle={{
                        sx: {
                          pl: "32px",
                          backgroundColor: "unset",
                          cursor: "unset",
                          ":hover": {
                            backgroundColor: "unset",
                          },
                        },
                      }}
                      label={
                        <Tooltip arrow disableInteractive title={child.name}>
                          <Box sx={{ maxWidth: "250px" }}>
                            <TypographyNormal
                              sx={{
                                fontSize: "15px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {child.name}
                            </TypographyNormal>
                          </Box>
                        </Tooltip>
                      }
                    />
                  ))}
              </CustomTreeItem>
            </SimpleTreeView>
          )}

          {content.type === "VIDEO" && (
            <Box display={"flex"} alignItems={"center"}>
              <TypographyNormal>
                {t("designer.contentVideoMuteLabel")}
              </TypographyNormal>
              <Checkbox
                checked={muteVideo === true}
                onChange={handleMuteVideoChange}
                sx={{
                  paddingY: 0,
                  paddingX: 1,
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Box
        display={"flex"}
        justifyContent={"end"}
        width={assetCount > 1 || content.appType === "ads" ? "30%" : "10%"}
      >
        <Box display={"flex"} gap={1}>
          {(assetCount > 1 || content.appType === "ads") && (
            <Tooltip
              arrow
              disableInteractive
              placement="top"
              title={t("designer.intervalFieldTip")}
            >
              <TextField
                sx={{ width: "100px" }}
                size="small"
                placeholder="0"
                value={interval}
                onChange={(e) => handleIntervalChange(e)}
                onKeyDown={(e) => inputOnlyNumbers(e)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {t("designer.contentIntervalSuffix")}
                    </InputAdornment>
                  ),
                }}
              />
            </Tooltip>
          )}
        </Box>

        <Box sx={{ mx: "2px" }}>
          <IconButton
            aria-controls={openMore ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={openMore ? "true" : undefined}
            onClick={handleClickMore}
            sx={{ color: colorStyling.black }}
          >
            <MoreVertIcon />
          </IconButton>

          <Menu
            id="basic-menu"
            anchorEl={anchorMore}
            open={openMore}
            onClose={handleCloseMore}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <MenuItem
              onClick={() => {
                handleOpenSetting();
                handleCloseMore();
              }}
              dense
            >
              <SettingsIcon fontSize="small" sx={{ mr: "5px" }} />
              {t("designer.settingBtn")}
            </MenuItem>
            <MenuItem
              onClick={() => {
                startDuplicateAsset();
                handleCloseMore();
              }}
              dense
            >
              <ContentCopyIcon fontSize="small" sx={{ mr: "5px" }} />
              {t("designer.duplicateBtn")}
            </MenuItem>
            <MenuItem
              sx={{ color: red[600] }}
              onClick={() => {
                removeContent(content.id);
                handleCloseMore();
              }}
              dense
            >
              <DeleteOutlinedIcon fontSize="small" sx={{ mr: "5px" }} />
              {t("designer.removeBtn")}
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </Box>
  );
};

export default ContentItem;
